/**
 * A per-request opt-out from the global error handling, for surfaces that render
 * their own failures on URLs a substring entry cannot claim.
 *
 * The Issues board reads and writes `/vulnerabilities/*` and `/sla-policies/*` —
 * the same paths the Vulnerabilities page uses — so an `ignoreErrorHandlingEndpointList`
 * entry would also silence that page's real failures. The Issues API client marks
 * its OWN requests instead: no global toast, and a 403 renders in place rather
 * than redirecting to the org dashboard.
 *
 * Kept apart from `SetupAxios` so an API module can import it without pulling in
 * the interceptors (and their toast and JSX dependencies).
 */
export const OWNS_ERRORS = { _ownsErrors: true } as const

/**
 * Narrower sibling of `OWNS_ERRORS` for requests that render a COMPONENT-ACCESS
 * refusal in place: the 403 `COMPONENT_NOT_PERMITTED`, the 503 "could not be
 * verified", the graph-save 400 naming locked nodes, and the role screen's own
 * 403s. Left to the global handlers these would toast a second time and — on a
 * scan 403 — eject the reader to the dashboard mid-wizard.
 *
 * Deliberately narrow: a quota or ordinary permission 403 on the same request
 * still gets the global treatment, so the caller only has to handle what it owns.
 */
export const OWNS_COMPONENT_REFUSAL = { _ownsComponentRefusal: true } as const

const COMPONENT_REFUSAL_CODE = 'COMPONENT_NOT_PERMITTED'

/** True when a flagged request failed with a component-access refusal. */
export function isOwnedComponentRefusal(
  status: number | undefined,
  data: unknown,
  config: { _ownsComponentRefusal?: boolean; url?: string } | undefined,
): boolean {
  if (config?._ownsComponentRefusal !== true) return false
  if (status === 503) return true
  const body = data as { code?: unknown; errors?: unknown } | undefined
  if (status === 403) {
    // The role screen's 403s carry only `detail`; everything else carries the code.
    return body?.code === COMPONENT_REFUSAL_CODE || (config.url ?? '').includes('/objects/component/')
  }
  const errors = body?.errors
  if (status === 400 && Array.isArray(errors)) {
    return errors.some((e: { code?: unknown }) => e?.code === COMPONENT_REFUSAL_CODE)
  }
  return false
}

declare module 'axios' {
  interface AxiosRequestConfig {
    _ownsErrors?: boolean
    _ownsComponentRefusal?: boolean
  }
}

/**
 * `WORKFLOW_NOT_RUNNABLE` (`2026-10-07-workflow-versioning.md`): the saved graph no
 * longer fits today's components. The orchestrator answers
 * `{code, message, details: {errors}}`; scan creation (Django) answers
 * `{workflow, validation_errors}`. Both carry per-step errors the caller lists in
 * a modal that links to the editor, so a generic toast on top would say it twice.
 *
 * Recognised by body, not by flag: the 400 can come from `POST /scan/`, a
 * schedule, or a run, none of which can be exempted wholesale without also
 * silencing their ordinary validation toasts.
 */
export function isOwnedNotRunnable(status: number | undefined, data: unknown): boolean {
  if (status !== 400 || !data || typeof data !== 'object') return false
  const body = data as { code?: unknown; validation_errors?: unknown }
  if (body.code === 'WORKFLOW_NOT_RUNNABLE') return true
  return Array.isArray(body.validation_errors) && body.validation_errors.length > 0
}
